// SDE.2 — Página do entregável: preview + rastreabilidade + exportação
// + versões + comparação editorial.

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  getDeliverable, generateDeliverable, listDeliverableVersions,
  exportDeliverable, compareDeliverableVersions,
} from "@/lib/sde/index.functions";
import { DeliverableRenderer } from "@/lib/sde/renderer";
import { trackDeliverableEvent } from "@/lib/sde/telemetry";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type { DeliverableStatus } from "@/lib/sde/types";
import {
  ArrowLeft, RefreshCw, FileDown, FileText, AlertCircle, History,
  ExternalLink, Printer, GitCompare, CheckCircle2, Lock, Play, XCircle, Clock,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/entregaveis/$key")({
  head: () => ({
    meta: [
      { title: "Entregável — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EntregavelPage,
});

function EntregavelPage() {
  const { key } = useParams({ from: "/_authenticated/membros/entregaveis/$key" });
  const enabled = useFeatureFlag("smart_deliverables");
  const qc = useQueryClient();

  const getFn = useServerFn(getDeliverable);
  const genFn = useServerFn(generateDeliverable);
  const versFn = useServerFn(listDeliverableVersions);
  const expFn = useServerFn(exportDeliverable);
  const cmpFn = useServerFn(compareDeliverableVersions);

  const [compare, setCompare] = useState<{ a: number; b: number } | null>(null);

  const q = useQuery({
    queryKey: ["sde", "detail", key],
    queryFn: () => getFn({ data: { key } }),
    enabled,
  });

  const versQ = useQuery({
    queryKey: ["sde", "versions", key],
    queryFn: () => versFn({ data: { key } }),
    enabled,
  });

  const cmpQ = useQuery({
    queryKey: ["sde", "compare", key, compare?.a, compare?.b],
    queryFn: () => cmpFn({ data: { key, a: compare!.a, b: compare!.b } }),
    enabled: enabled && !!compare,
  });

  const gen = useMutation({
    mutationFn: () => genFn({ data: { key } }),
    onSuccess: (res) => {
      trackDeliverableEvent((res as any)?.version === 1 ? "sde.deliverable.generated" : "sde.deliverable.regenerated", {
        key, version: (res as any)?.version,
      });
      qc.invalidateQueries({ queryKey: ["sde"] });
    },
  });

  const exportAs = useMutation({
    mutationFn: (format: "html" | "docx") => expFn({ data: { key, format } }),
    onSuccess: (res, format) => {
      downloadBase64(res.base64, res.filename, res.mime);
      trackDeliverableEvent("sde.deliverable.exported", { key, format });
    },
  });

  const twoLatest = useMemo(() => {
    const versions = versQ.data?.versions ?? [];
    if (versions.length < 2) return null;
    return { a: versions[1].version, b: versions[0].version }; // penúltima vs. atual
  }, [versQ.data]);

  if (!enabled) return <div className="p-8 text-center text-sm text-slate-500">Camada indisponível.</div>;
  if (q.isLoading || !q.data) return <div className="p-8 text-sm text-slate-500">Carregando…</div>;

  const { entry, doc, status, savedVersion, reason, missingRequired, gaps, usedBlocks } = q.data;

  // Estado sem preview possível
  if (!doc) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="mb-6">
          <Link to="/membros/entregaveis" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
            <ArrowLeft size={12} /> Voltar para Entregáveis
          </Link>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <StatusIcon status={status} className="mx-auto h-8 w-8 text-slate-400" />
          <h1 className="mt-3 text-xl font-semibold text-slate-800">{entry.title}</h1>
          <p className="mt-2 text-sm text-slate-600">{reason}</p>
          {missingRequired.length > 0 ? (
            <div className="mx-auto mt-6 max-w-md rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-sm">
              <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Seções obrigatórias faltantes</p>
              <ul className="space-y-1">
                {missingRequired.map((m) => (
                  <li key={m.block}>
                    <Link
                      to="/membros/meu-negocio/secao/$section"
                      params={{ section: m.block }}
                      className="inline-flex items-center gap-1 text-[#0C1B33] hover:underline"
                    >
                      {m.label} <ExternalLink size={11} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <Link to="/membros/meu-negocio" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0C1B33] px-4 py-2 text-sm text-white">
            Ir para Meu Negócio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Link to="/membros/entregaveis" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft size={12} /> Voltar para Entregáveis
        </Link>
        <StatusBadge status={status} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Preview */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm print:border-0 print:shadow-none">
          <div className="border-b border-slate-100 p-4 flex items-center justify-between print:hidden">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Preview editorial</p>
              <h2 className="text-sm font-semibold text-slate-800">{doc.title}</h2>
            </div>
            <StatusBadge status={status} />
          </div>
          <div className="p-6 sm:p-10">
            <DeliverableRenderer doc={doc} />
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 print:hidden">
          {reason ? (
            <div className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${
              status === "outdated" ? "border-amber-200 bg-amber-50 text-amber-900" :
              status === "insufficient_data" ? "border-slate-200 bg-slate-50 text-slate-700" :
              status === "error" ? "border-red-200 bg-red-50 text-red-800" :
              "border-emerald-200 bg-emerald-50 text-emerald-900"
            }`}>
              <StatusIcon status={status} className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{reason}</p>
            </div>
          ) : null}

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Ações</p>
            <div className="mt-3 space-y-2">
              <button
                onClick={() => gen.mutate()}
                disabled={gen.isPending || status === "insufficient_data" || status === "locked"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0C1B33] px-3 py-2 text-sm font-medium text-white hover:bg-[#08152a] disabled:opacity-50"
              >
                <RefreshCw size={14} className={gen.isPending ? "animate-spin" : ""} />
                {savedVersion === 0 ? "Gerar e salvar v1" : status === "outdated" ? "Atualizar agora" : "Regenerar"}
              </button>
              <button
                onClick={() => exportAs.mutate("docx")}
                disabled={exportAs.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
              >
                <FileDown size={14} /> Exportar .docx
              </button>
              <button
                onClick={() => exportAs.mutate("html")}
                disabled={exportAs.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
              >
                <FileDown size={14} /> Exportar .html
              </button>
              <button
                onClick={() => { trackDeliverableEvent("sde.deliverable.printed", { key }); window.print(); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
              >
                <Printer size={14} /> Imprimir / PDF
              </button>
            </div>
          </div>

          {/* Rastreabilidade */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Construído a partir de</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {entry.sourceBlocks.map((bk) => {
                const used = usedBlocks.includes(bk);
                return (
                  <li key={bk} className="flex items-center justify-between gap-2">
                    <Link
                      to="/membros/meu-negocio/secao/$section"
                      params={{ section: bk }}
                      className={`inline-flex items-center gap-1 hover:underline ${used ? "text-[#0C1B33]" : "text-slate-400"}`}
                    >
                      {bk} <ExternalLink size={11} />
                    </Link>
                    {used ? (
                      <CheckCircle2 size={12} className="text-emerald-600" />
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide text-amber-700">vazio</span>
                    )}
                  </li>
                );
              })}
            </ul>
            {gaps.length > 0 ? (
              <p className="mt-3 text-[11px] text-amber-700">
                {gaps.length} capítulo(s) ainda vazio(s) — preencha para enriquecer o documento.
              </p>
            ) : (
              <p className="mt-3 text-[11px] text-emerald-700">Todos os capítulos-fonte preenchidos.</p>
            )}
          </div>

          {/* Seções editoriais esperadas */}
          {entry.editorialSections && entry.editorialSections.length > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Estrutura editorial</p>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {entry.editorialSections.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          ) : null}

          {/* Versões */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                <History size={12} /> Versões
              </p>
              {twoLatest ? (
                <button
                  onClick={() => { setCompare(twoLatest); trackDeliverableEvent("sde.deliverable.compared", { key }); }}
                  className="inline-flex items-center gap-1 text-[11px] text-[#0C1B33] hover:underline"
                >
                  <GitCompare size={11} /> Comparar
                </button>
              ) : null}
            </div>
            {versQ.data && versQ.data.versions.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {versQ.data.versions.map((v) => (
                  <li key={v.version} className="flex items-start justify-between gap-2">
                    <span>
                      v{v.version}
                      {v.diffSummary?.reason ? (
                        <span className="ml-1 text-[10px] text-slate-400">· {String(v.diffSummary.reason)}</span>
                      ) : null}
                    </span>
                    <span className="text-slate-400">{new Date(v.createdAt).toLocaleDateString("pt-BR")}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-slate-500">Nenhuma versão salva ainda.</p>
            )}
          </div>
        </aside>
      </div>

      {/* Compare dialog */}
      {compare ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden" onClick={() => setCompare(null)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Comparar versões</h2>
              <p className="mt-1 text-xs text-slate-600">v{compare.a} × v{compare.b}</p>
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-6 py-4">
              {cmpQ.isLoading ? (
                <p className="text-sm text-slate-500">Analisando…</p>
              ) : cmpQ.data ? (
                <ul className="divide-y divide-slate-100">
                  {cmpQ.data.sectionDiff.map((s) => (
                    <li key={s.heading} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-800">{s.heading}</span>
                      <SectionDiffBadge state={s.state} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
              <button onClick={() => setCompare(null)} className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }: { status: DeliverableStatus }) {
  const map: Record<DeliverableStatus, { cls: string; label: string }> = {
    ready:             { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Pronto" },
    outdated:          { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "Desatualizado" },
    available:         { cls: "bg-blue-50 text-blue-700 border-blue-200",          label: "Pronto para gerar" },
    generating:        { cls: "bg-blue-50 text-blue-700 border-blue-200",          label: "Gerando…" },
    insufficient_data: { cls: "bg-slate-50 text-slate-600 border-slate-200",       label: "Dados insuficientes" },
    locked:            { cls: "bg-slate-50 text-slate-500 border-slate-200",       label: "Bloqueado" },
    error:             { cls: "bg-red-50 text-red-700 border-red-200",             label: "Erro" },
  };
  const m = map[status];
  return <span className={`rounded border px-2 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
}

function StatusIcon({ status, className }: { status: DeliverableStatus; className?: string }) {
  const Icon =
    status === "ready" ? CheckCircle2 :
    status === "outdated" ? RefreshCw :
    status === "available" ? Play :
    status === "generating" ? Clock :
    status === "locked" ? Lock :
    status === "error" ? XCircle :
    AlertCircle;
  return <Icon className={className} />;
}

function SectionDiffBadge({ state }: { state: "added" | "removed" | "changed" | "unchanged" }) {
  const map = {
    added:     { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "adicionada" },
    removed:   { cls: "bg-red-50 text-red-700 border-red-200",             label: "removida" },
    changed:   { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "alterada" },
    unchanged: { cls: "bg-slate-50 text-slate-500 border-slate-200",       label: "sem mudanças" },
  } as const;
  const m = map[state];
  return <span className={`rounded border px-2 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
}

function downloadBase64(base64: string, filename: string, mime: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
